import { createSavedGames } from './saved-games.js';
import { requestApi } from './api.js';
import { translate, formatNumber, getLocale } from './translations.js';

const el = id => document.getElementById(id);
let player;
let actions;
let googleNonce;
const scripts = new Map();

function loadScript(url) {
  if (!scripts.has(url)) scripts.set(url, new Promise((resolve, reject) => {
    const script = document.createElement('script'); script.src = url; script.async = true;
    script.onload = resolve; script.onerror = () => { scripts.delete(url); script.remove(); reject(new Error('signin_unavailable')); };
    document.head.appendChild(script);
  }));
  return scripts.get(url);
}

function errorMessage(error) { el('profile-message').textContent = translate(error.code ?? (error.message === 'signin_unavailable' ? 'signin_unavailable' : 'network_error')); }

export function updatePlayerActions(game) {
  if (!player) return;
  el('player-name').textContent = player.nickname || translate(player.signedIn ? 'playerAccount' : 'guestPlayer');
  el('player-status').textContent = translate(player.signedIn ? 'accountConnected' : 'guestProgressHint');
  el('nickname').value = player.nickname;
  el('logout-player').hidden = !player.signedIn;
  el('save-nickname').hidden = !player.signedIn;
  el('nickname-field').hidden = !player.signedIn;
  el('google-signin').hidden = player.signedIn || !player.providers.googleClientId;
  el('apple-signin').hidden = player.signedIn || !player.providers.appleClientId;
  el('signin-unavailable').hidden = player.signedIn || Boolean(player.providers.googleClientId || player.providers.appleClientId);
  el('publish-result').hidden = game?.status !== 'completed' || game?.mode !== 'daily';
  el('practice-ranking-hint').hidden = game?.status !== 'completed' || game?.mode !== 'practice';
  el('publish-profile-result').hidden = !player.signedIn || game?.status !== 'completed' || game?.mode !== 'daily';
}

export async function refreshPlayer() {
  player = await requestApi('/player'); updatePlayerActions(actions.getGame()); return player;
}

async function completeLogin(provider, idToken, nonce, code) {
  try {
    await requestApi(`/auth/${provider}`, { method: 'POST', body: { idToken, nonce, code } });
    await refreshPlayer();
    el('profile-message').textContent = translate('signedInSuccess');
  } catch (error) { errorMessage(error); }
}

export async function openPlayerProfile() {
  actions.openDialog('player-dialog'); el('profile-message').textContent = '';
  try {
    await refreshPlayer();
    if (!player.signedIn && player.providers.googleClientId) {
      await loadScript('https://accounts.google.com/gsi/client');
      googleNonce = (await requestApi('/auth/google/challenge', { method: 'POST', body: {} })).nonce;
      window.google.accounts.id.initialize({ client_id: player.providers.googleClientId, nonce: googleNonce,
        callback: response => completeLogin('google', response.credential, googleNonce), auto_select: false });
      el('google-signin').replaceChildren();
      window.google.accounts.id.renderButton(el('google-signin'), { theme: 'outline', size: 'large', width: 260 });
    }
    if (!player.signedIn && player.providers.appleClientId) {
      el('apple-signin').disabled = true;
      await loadScript('https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js');
      el('apple-signin').disabled = false;
    }
  } catch (error) { errorMessage(error); }
}

async function showLeaderboard() {
  actions.openDialog('leaderboard-dialog');
  el('leaderboard-list').replaceChildren(); el('leaderboard-message').textContent = translate('loading');
  try {
    const game = document.body.dataset.view === 'game' ? actions.getGame() : null;
    const languageCode = game?.language.code ?? getLocale();
    const dayQuery = game?.mode === 'daily' ? `&day=${encodeURIComponent(game.challengeDay)}` : '';
    const data = await requestApi(`/leaderboard?languageCode=${encodeURIComponent(languageCode)}${dayQuery}`);
    el('leaderboard-day').textContent = `${data.day} · ${translate('tbilisiTime')}`;
    el('leaderboard-message').textContent = data.entries.length ? '' : translate('leaderboardEmpty');
    for (const entry of data.entries) {
      const item = document.createElement('li'); item.classList.toggle('leaderboard-you', entry.isYou);
      const rank = document.createElement('span'); rank.className = 'leaderboard-rank'; rank.textContent = formatNumber(entry.rank);
      const name = document.createElement('span'); name.textContent = entry.nickname + (entry.isYou ? ` · ${translate('you')}` : '');
      const score = document.createElement('strong'); score.textContent = `${formatNumber(entry.percentage)}%`;
      item.append(rank, name, score); el('leaderboard-list').appendChild(item);
    }
    if (data.yourEntry) el('leaderboard-message').textContent = translate('yourRank', { rank: formatNumber(data.yourEntry.rank) });
  } catch (error) { el('leaderboard-message').textContent = translate(error.code ?? 'network_error'); }
}

export function setupPlayerUi(options) {
  actions = options;
  for (const id of ['open-player', 'publish-result']) el(id).addEventListener('click', async () => {
    if (id === 'open-player') return openPlayerProfile();
    try {
      await refreshPlayer();
      if (!player.signedIn || !player.nickname) return openPlayerProfile();
      await requestApi('/leaderboard', { method: 'POST', body: { gameId: actions.getGame().id } });
      await showLeaderboard();
    } catch (error) { console.warn('Leaderboard publication failed:', error.code ?? 'network_error'); }
  });
  for (const id of ['open-leaderboard', 'menu-leaderboard']) el(id).addEventListener('click', showLeaderboard);
  el('daily-game').addEventListener('click', actions.playDaily);
  el('apple-signin').addEventListener('click', async () => {
    try {
      const { nonce } = await requestApi('/auth/apple/challenge', { method: 'POST', body: {} });
      window.AppleID.auth.init({ clientId: player.providers.appleClientId, redirectURI: player.providers.appleRedirectUri, state: nonce, nonce, usePopup: true });
      const result = await window.AppleID.auth.signIn();
      if (result.authorization.state !== nonce) throw new Error('invalid_login');
      await completeLogin('apple', result.authorization.id_token, nonce, result.authorization.code);
    } catch (error) { errorMessage(error); }
  });
  el('save-nickname').addEventListener('click', async () => {
    try { player = await requestApi('/player', { method: 'PATCH', body: { nickname: el('nickname').value } }); updatePlayerActions(actions.getGame()); el('profile-message').textContent = translate('nicknameSaved'); }
    catch (error) { errorMessage(error); }
  });
  el('publish-profile-result').addEventListener('click', async () => {
    try {
      await requestApi('/player', { method: 'PATCH', body: { nickname: el('nickname').value } });
      await refreshPlayer();
      await requestApi('/leaderboard', { method: 'POST', body: { gameId: actions.getGame()?.id } });
      await showLeaderboard();
    } catch (error) { errorMessage(error); }
  });
  el('logout-player').addEventListener('click', async () => {
    try { await requestApi('/player/logout', { method: 'POST', body: {} }); createSavedGames(localStorage).clear(); location.reload(); }
    catch (error) { errorMessage(error); }
  });
  el('delete-player').addEventListener('click', () => actions.openDialog('delete-player-dialog'));
  el('confirm-delete-player').addEventListener('click', async () => {
    el('confirm-delete-player').disabled = true;
    try { await requestApi('/player', { method: 'DELETE', body: {} }); createSavedGames(localStorage).clear(); location.reload(); }
    catch (error) { el('delete-player-message').textContent = translate(error.code ?? 'network_error'); }
    finally { el('confirm-delete-player').disabled = false; }
  });
}
